import { useEffect, useState } from "react";
import { adminApi } from "../api.ts";
import { getGmailSenderAuthCode } from "../../../lib/googleToken.ts";

export function MailerSettingsSection() {
  const [status, setStatus] = useState<{
    activeProvider: "gmail" | "resend" | "sender" | "none";
    senderEmail: string;
    senderName: string;
    hasGmail: boolean;
    hasResend: boolean;
    hasSenderNet: boolean;
    lastTestStatus?: { success: boolean; message: string; timestamp: string };
    updatedAt?: string;
  } | null>(null);

  const [loading, setLoading] = useState(true);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [resendKey, setResendKey] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [testEmail, setTestEmail] = useState("moshe157@gmail.com");
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  async function loadStatus() {
    setLoading(true);
    try {
      const s = await adminApi.getMailerStatus();
      setStatus(s);
      if (s.senderEmail) setSenderEmail(s.senderEmail);
    } catch (e) {
      console.error("Failed to load mailer status", e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadStatus();

    // Check if redirected from Google OAuth with ?code=
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code) {
      setConnectingGoogle(true);
      adminApi
        .connectGmailMailer(code, `${window.location.origin}/admin`)
        .then((res) => {
          alert(`✓ Gmail Sender successfully connected as ${res.senderEmail}!`);
          // Clear query params from URL
          window.history.replaceState({}, document.title, window.location.pathname);
          void loadStatus();
        })
        .catch((err) => {
          alert(`Google connection failed: ${(err as Error).message}`);
        })
        .finally(() => setConnectingGoogle(false));
    }
  }, []);

  async function handleConnectGooglePopup() {
    setConnectingGoogle(true);
    try {
      const code = await getGmailSenderAuthCode();
      const res = await adminApi.connectGmailMailer(code, "postmessage");
      alert(`✓ Gmail Sender successfully connected as ${res.senderEmail}!`);
      await loadStatus();
    } catch (err: any) {
      console.warn("GIS popup error, falling back to redirect:", err);
      // Fallback: Open full Google OAuth redirect
      const clientId = "1037800895337-97de77ttdpvbh91fkdnujvqtr0f4timf.apps.googleusercontent.com";
      const redirectUri = `${window.location.origin}/admin`;
      const scope = encodeURIComponent("https://www.googleapis.com/auth/gmail.send email");
      const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(
        redirectUri
      )}&response_type=code&scope=${scope}&access_type=offline&prompt=consent`;
      window.location.href = url;
    } finally {
      setConnectingGoogle(false);
    }
  }

  async function handleSaveResend() {
    if (!resendKey.trim()) return;
    setSavingKey(true);
    try {
      await adminApi.saveMailerConfig({
        resendApiKey: resendKey.trim(),
        senderEmail: senderEmail.trim() || undefined,
      });
      alert("✓ Mailer configuration saved!");
      await loadStatus();
    } catch (err: any) {
      alert(`Save failed: ${err.message}`);
    } finally {
      setSavingKey(false);
    }
  }

  async function handleSendTest() {
    if (!testEmail.trim() || !testEmail.includes("@")) {
      alert("Enter a valid test email destination.");
      return;
    }
    setSendingTest(true);
    setTestResult(null);
    try {
      const res = await adminApi.sendTestEmail(testEmail.trim());
      setTestResult({
        success: res.ok,
        message: res.ok
          ? `✓ Test email sent successfully to ${testEmail}! Check your inbox (in English).`
          : `Failed: ${res.message}`,
      });
      await loadStatus();
    } catch (err: any) {
      setTestResult({
        success: false,
        message: `Error: ${err.message}`,
      });
    } finally {
      setSendingTest(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Status Card */}
      <section
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          padding: 20,
          boxShadow: "var(--sh-1)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--text-1)" }}>
            Transactional Email Sender Status
          </h3>
          <button
            type="button"
            className="pill-btn ghost"
            style={{ fontSize: 12 }}
            onClick={() => void loadStatus()}
          >
            Refresh Status
          </button>
        </div>

        {loading ? (
          <div style={{ color: "var(--text-3)", fontSize: 13 }}>Checking mailer configuration…</div>
        ) : status?.hasGmail ? (
          <div
            style={{
              padding: 14,
              borderRadius: 8,
              background: "rgba(16, 185, 129, 0.1)",
              border: "1px solid rgba(16, 185, 129, 0.3)",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span style={{ fontSize: 20 }}>✓</span>
            <div>
              <div style={{ fontWeight: 700, color: "#10b981", fontSize: 14 }}>
                Active Sender: Gmail API ({status.senderEmail || "moshe@echotora.com"})
              </div>
              <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 2 }}>
                All workspace invitation and notification emails are sent in English directly via Gmail API.
              </div>
            </div>
          </div>
        ) : status?.hasResend ? (
          <div
            style={{
              padding: 14,
              borderRadius: 8,
              background: "rgba(16, 185, 129, 0.1)",
              border: "1px solid rgba(16, 185, 129, 0.3)",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span style={{ fontSize: 20 }}>✓</span>
            <div>
              <div style={{ fontWeight: 700, color: "#10b981", fontSize: 14 }}>
                Active Sender: Resend API ({status.senderEmail || "onboarding@resend.dev"})
              </div>
              <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 2 }}>
                Emails are sent in English through Resend transactional service.
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: 14,
              borderRadius: 8,
              background: "rgba(239, 68, 68, 0.08)",
              border: "1px solid rgba(239, 68, 68, 0.25)",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span style={{ fontSize: 20 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 700, color: "var(--gc-red, #ef4444)", fontSize: 14 }}>
                Email Sender Not Connected
              </div>
              <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 2 }}>
                Invitation emails cannot be delivered until you connect a Google account or enter a Resend API key below.
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Connection Options */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Option 1: Google Account */}
        <section
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: 20,
            boxShadow: "var(--sh-1)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 18 }}>🔷</span>
              <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--text-1)" }}>
                Option 1: Connect Google / Gmail (Recommended)
              </h4>
            </div>
            <p style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.5, margin: "0 0 16px" }}>
              Authorize SellSynq to send transactional invitation emails directly as <strong>moshe@echotora.com</strong> via Google's official Gmail API. 100% deliverability, no DNS or domain verification needed.
            </p>
          </div>

          <button
            type="button"
            className="pill-btn primary"
            disabled={connectingGoogle}
            onClick={() => void handleConnectGooglePopup()}
            style={{ width: "100%", justifyContent: "center", padding: "10px 16px", fontSize: 13 }}
          >
            {connectingGoogle ? "Connecting Google Account…" : "Connect Google Account (1-Click)"}
          </button>
        </section>

        {/* Option 2: Resend API Key */}
        <section
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: 20,
            boxShadow: "var(--sh-1)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 18 }}>🖤</span>
              <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--text-1)" }}>
                Option 2: Resend API Key
              </h4>
            </div>
            <p style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.5, margin: "0 0 12px" }}>
              Alternatively, paste an API key from <a href="https://resend.com" target="_blank" rel="noreferrer" style={{ color: "var(--primary)" }}>resend.com</a> (free 3,000 emails/month).
            </p>
            <input
              type="password"
              placeholder="re_123456789..."
              value={resendKey}
              onChange={(e) => setResendKey(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid var(--border)",
                background: "var(--surface-hover)",
                color: "var(--text-1)",
                fontSize: 13,
                marginBottom: 12,
              }}
            />
          </div>

          <button
            type="button"
            className="pill-btn ghost"
            disabled={savingKey || !resendKey.trim()}
            onClick={() => void handleSaveResend()}
            style={{ width: "100%", justifyContent: "center", padding: "10px 16px", fontSize: 13 }}
          >
            {savingKey ? "Saving…" : "Save Resend Key"}
          </button>
        </section>
      </div>

      {/* Send Test Email Section */}
      <section
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          padding: 20,
          boxShadow: "var(--sh-1)",
        }}
      >
        <h4 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700, color: "var(--text-1)" }}>
          ✉️ Send Test Invitation Email
        </h4>
        <p style={{ fontSize: 13, color: "var(--text-2)", margin: "0 0 14px" }}>
          Test that system emails are delivering properly in English.
        </p>

        <div style={{ display: "flex", gap: 10, maxWidth: 500 }}>
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="colleague@domain.com"
            style={{
              flex: 1,
              padding: "9px 12px",
              borderRadius: 8,
              border: "1px solid var(--border)",
              background: "var(--surface-hover)",
              color: "var(--text-1)",
              fontSize: 13,
            }}
          />
          <button
            type="button"
            className="pill-btn primary"
            disabled={sendingTest || !testEmail.trim()}
            onClick={() => void handleSendTest()}
          >
            {sendingTest ? "Sending…" : "Send Test Email"}
          </button>
        </div>

        {testResult && (
          <div
            style={{
              marginTop: 14,
              padding: "10px 14px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              background: testResult.success ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.08)",
              color: testResult.success ? "#10b981" : "var(--gc-red, #ef4444)",
              border: testResult.success
                ? "1px solid rgba(16, 185, 129, 0.3)"
                : "1px solid rgba(239, 68, 68, 0.25)",
            }}
          >
            {testResult.message}
          </div>
        )}
      </section>
    </div>
  );
}
